// Service logo detection and management
export interface ServiceLogo {
  name: string;
  domains: string[];
  logoUrl: string;
  fallbackIcon: string;
}

// Common service logos
export const serviceLogos: ServiceLogo[] = [
  {
    name: 'ChatGPT',
    domains: ['openai.com', 'chat.openai.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/openai.svg',
    fallbackIcon: 'chat'
  },
  {
    name: 'Google',
    domains: ['google.com', 'gmail.com', 'drive.google.com', 'docs.google.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/google.svg',
    fallbackIcon: 'search'
  },
  {
    name: 'Microsoft',
    domains: ['microsoft.com', 'office.com', 'outlook.com', 'teams.microsoft.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/microsoft.svg',
    fallbackIcon: 'business'
  },
  {
    name: 'GitHub',
    domains: ['github.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/github.svg',
    fallbackIcon: 'code'
  },
  {
    name: 'Slack',
    domains: ['slack.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/slack.svg',
    fallbackIcon: 'chat'
  },
  {
    name: 'Discord',
    domains: ['discord.com', 'discord.gg'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/discord.svg',
    fallbackIcon: 'chat'
  },
  {
    name: 'YouTube',
    domains: ['youtube.com', 'youtu.be'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/youtube.svg',
    fallbackIcon: 'localMovies'
  },
  {
    name: 'Netflix',
    domains: ['netflix.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/netflix.svg',
    fallbackIcon: 'localMovies'
  },
  {
    name: 'Amazon',
    domains: ['amazon.com', 'aws.amazon.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/amazon.svg',
    fallbackIcon: 'shoppingCart'
  },
  {
    name: 'Facebook',
    domains: ['facebook.com', 'fb.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/facebook.svg',
    fallbackIcon: 'public'
  },
  {
    name: 'Twitter',
    domains: ['twitter.com', 'x.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/x.svg',
    fallbackIcon: 'public'
  },
  {
    name: 'LinkedIn',
    domains: ['linkedin.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/linkedin.svg',
    fallbackIcon: 'work'
  },
  {
    name: 'Notion',
    domains: ['notion.so', 'notion.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/notion.svg',
    fallbackIcon: 'edit'
  },
  {
    name: 'Trello',
    domains: ['trello.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/trello.svg',
    fallbackIcon: 'viewModule'
  },
  {
    name: 'Figma',
    domains: ['figma.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/figma.svg',
    fallbackIcon: 'edit'
  },
  {
    name: 'Spotify',
    domains: ['spotify.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/spotify.svg',
    fallbackIcon: 'musicNote'
  },
  {
    name: 'Dropbox',
    domains: ['dropbox.com'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/dropbox.svg',
    fallbackIcon: 'cloudDownload'
  },
  {
    name: 'Zoom',
    domains: ['zoom.us'],
    logoUrl: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/zoom.svg',
    fallbackIcon: 'videocam'
  }
];

/**
 * Extract domain from URL
 */
export const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.toLowerCase().replace('www.', '');
  } catch {
    return '';
  }
};

/**
 * Detect service logo based on URL
 */
export const detectServiceLogo = (url: string, title?: string): ServiceLogo | null => {
  const domain = extractDomain(url);
  
  // First try exact domain match
  let matchedService = serviceLogos.find(service => 
    service.domains.includes(domain)
  );
  
  // If no exact match, try partial domain match
  if (!matchedService && domain) {
    matchedService = serviceLogos.find(service => 
      service.domains.some(serviceDomain => 
        domain.includes(serviceDomain.replace('.com', '')) ||
        serviceDomain.includes(domain.replace('.com', ''))
      )
    );
  }
  
  // If still no match, try title-based matching
  if (!matchedService && title) {
    const titleLower = title.toLowerCase();
    matchedService = serviceLogos.find(service => 
      titleLower.includes(service.name.toLowerCase())
    );
  }
  
  return matchedService || null;
};

/**
 * Get icon configuration for a tile
 */
export interface TileIcon {
  type: 'material' | 'service' | 'custom';
  value: string; // icon name for material, URL for service/custom
  fallback?: string; // fallback material icon
}

/**
 * Get favicon URL using Google's S2 API
 */
export const getFaviconUrl = (url: string, size: number = 32): string => {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`;
  } catch (error) {
    console.warn('Invalid URL for favicon detection:', url);
    return '';
  }
};

export const getTileIcon = (url: string, title?: string, customIcon?: string): TileIcon => {
  // If user has set a custom icon, use that
  if (customIcon) {
    if (customIcon.startsWith('http') || customIcon.startsWith('data:')) {
      return { type: 'custom', value: customIcon };
    } else {
      return { type: 'material', value: customIcon };
    }
  }
  
  // If we have a URL, always try Google's favicon API first (this gives the best results)
  if (url && url.startsWith('http')) {
    const faviconUrl = getFaviconUrl(url, 32);
    if (faviconUrl) {
      return {
        type: 'service',
        value: faviconUrl,
        fallback: 'link'
      };
    }
  }
  
  // Fallback to known service logos if Google API fails
  const detectedService = detectServiceLogo(url, title);
  if (detectedService) {
    return {
      type: 'service',
      value: detectedService.logoUrl,
      fallback: detectedService.fallbackIcon
    };
  }
  
  // Default fallback
  return { type: 'material', value: 'link' };
};

/**
 * Check if an image URL is accessible
 */
export const checkImageAccessibility = async (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
    
    // Timeout after 5 seconds
    setTimeout(() => resolve(false), 5000);
  });
};